/**
 * @module  public/js/utils/firestoreHelpers.js
 * @summary Front-end helpers to scope Firestore paths by tenant.
 *
 * @description
 * Mirrors the server-side utilities used by Cloud Functions so that
 * browser code can easily construct tenant-aware DocumentReferences and
 * writes.  These helpers simply prefix unscoped paths with
 * `tenants/<tenantId>/` and automatically inject a `tenantId` field on
 * writes when missing.
 *
 * ## Exports
 * – `docWithTenant(tenantId, path)` – returns a DocumentReference
 * – `colWithTenant(tenantId, path)` – returns a CollectionReference
 * – `colGroupWithTenant(collectionId)` – returns a collection group Query
 * – `writeWithTenant(tenantId, docRef, data, opts?)` – wrapper around `setDoc`
 *
 * @version 1.0.0
 * @since   2025-05-22
 */

import {
  doc,
  setDoc,
  collection,
  collectionGroup,
  updateDoc,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { db, getDbForTenant } from '../core/firebase-service.js';

/* eslint-disable import/prefer-default-export */

/**
 * Create a DocumentReference scoped to the tenant.
 * If `path` already begins with `tenants/` it is returned as-is.
 *
 * @param {string} tenantId
 * @param {string} path
 * @returns {import('firebase/firestore').DocumentReference}
 */
export function docWithTenant(tenantId, path) {
  const scoped = path.startsWith('tenants/')
    ? path
    : `tenants/${tenantId}/${path}`;
  const tenantDb = getDbForTenant(tenantId);
  return doc(tenantDb, ...scoped.split('/'));
}

/**
 * Create a CollectionReference scoped to the tenant.
 *
 * @param {string} tenantId
 * @param {string} path
 * @returns {import('firebase/firestore').CollectionReference}
 */
export function colWithTenant(tenantId, path) {
  const scoped = path.startsWith('tenants/')
    ? path
    : `tenants/${tenantId}/${path}`;
  const tenantDb = getDbForTenant(tenantId);
  return collection(tenantDb, ...scoped.split('/'));
}

/**
 * Set a document ensuring the payload contains a `tenantId` field.
 *
 * @param {string} tenantId
 * @param {import('firebase/firestore').DocumentReference} docRef
 * @param {object} data
 * @param {import('firebase/firestore').SetOptions} [opts={}]
 * @returns {Promise<void>}
 */
export async function writeWithTenant(tenantId, docRef, data, opts = {}) {
  const payload = data.tenantId ? data : { tenantId, ...data };
  await setDoc(docRef, payload, opts);
}

/**
 * Update a document ensuring the payload contains a `tenantId` field.
 * Supports additional field/value tuples (e.g., `FieldPath` deletes) via the
 * `fieldValuePairs` array. Each tuple is flattened into the varargs form of
 * `updateDoc` so callers can target literal dotted field names safely.
 *
 * @param {string} tenantId
 * @param {import('firebase/firestore').DocumentReference} docRef
 * @param {object} data
 * @param {Array<[string | import('firebase/firestore').FieldPath, any]>} [fieldValuePairs=[]]
 * @returns {Promise<void>}
 */
export async function updateWithTenant(
  tenantId,
  docRef,
  data,
  fieldValuePairs = []
) {
  const payload = data.tenantId ? data : { tenantId, ...data };
  const extra = fieldValuePairs.flatMap(([field, value]) => [field, value]);
  await updateDoc(docRef, payload, ...extra);
}

/**
 * Retrieve a collection group reference scoped to all tenants.
 * This mirrors the server-side `colGroupWithTenant` helper.
 *
 * @param {string} collectionId
 * @returns {import('firebase/firestore').Query}
 */
export function colGroupWithTenant(collectionId) {
  return collectionGroup(db, collectionId);
}

/**
 * Create a DocumentReference scoped to the shared global namespace.
 *
 * @param {string} path
 * @returns {import('firebase/firestore').DocumentReference}
 */
export function docGlobal(path) {
  return doc(db, ...path.split('/'));
}

/**
 * Create a CollectionReference scoped to the shared global namespace.
 *
 * @param {string} path
 * @returns {import('firebase/firestore').CollectionReference}
 */
export function colGlobal(path) {
  return collection(db, ...path.split('/'));
}

/**
 * Write to a global document without injecting tenant metadata.
 *
 * @param {import('firebase/firestore').DocumentReference} docRef
 * @param {object} data
 * @param {import('firebase/firestore').SetOptions} [opts={}]
 * @returns {Promise<void>}
 */
export async function writeGlobal(docRef, data, opts = {}) {
  await setDoc(docRef, data, opts);
}
